import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard } from 'lucide-react';
import { LOGO_ICON_BLUE } from '@/components/SplashScreen';

export default function PaymentGatewayPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/')}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <img src={LOGO_ICON_BLUE} alt="Rebuilt" className="h-8 w-8" />
          <h1 className="text-xl font-bold text-gray-900">Payment Gateway Policy</h1>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-2xl shadow-sm p-8 md:p-12">
          <div className="flex items-center gap-3 mb-6">
            <CreditCard className="h-8 w-8 text-blue-600" />
            <h2 className="text-3xl font-bold text-gray-900">Payment Gateway Policy</h2>
          </div>

          <p className="text-sm text-gray-500 mb-8">
            <strong>Last Updated:</strong> January 1, 2025
          </p>

          <div className="prose prose-gray max-w-none space-y-8">
            {/* Introduction */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">1. Introduction</h3>
              <p className="text-gray-700 leading-relaxed">
                This Payment Gateway Policy explains how Rebuilt processes payments for loan disbursements, EMI repayments, and other financial transactions through secure payment gateways. We are committed to ensuring safe, secure, and seamless payment experiences for all our users.
              </p>
            </section>

            {/* Payment Gateway Partners */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">2. Payment Gateway Partners</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                Rebuilt partners with the following RBI-approved and PCI-DSS compliant payment gateways:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Razorpay:</strong> Primary payment gateway for loan disbursements and repayments</li>
                <li><strong>Razorpay:</strong> Backup payment gateway for redundancy</li>
                <li><strong>Bank Transfer (NEFT/RTGS/IMPS):</strong> Direct bank transfer option</li>
                <li><strong>UPI:</strong> Unified Payments Interface for instant transfers</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                All payment gateways used by Rebuilt comply with Reserve Bank of India (RBI) guidelines and international security standards.
              </p>
            </section>

            {/* Supported Payment Methods */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">3. Supported Payment Methods</h3>
              
              <p className="text-gray-700 font-semibold mb-3">3.1 For Loan Disbursement</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li><strong>Direct Bank Transfer:</strong> Funds transferred directly to your registered bank account via NEFT/IMPS</li>
                <li><strong>Processing Time:</strong> Instant to 2 business days depending on bank</li>
              </ul>

              <p className="text-gray-700 font-semibold mb-3">3.2 For EMI Repayment</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Debit Cards:</strong> All major banks (Visa, Mastercard, RuPay)</li>
                <li><strong>Credit Cards:</strong> Visa, Mastercard, American Express</li>
                <li><strong>Net Banking:</strong> 50+ banks supported</li>
                <li><strong>UPI:</strong> Google Pay, PhonePe, Paytm, BHIM, and other UPI apps</li>
                <li><strong>Wallets:</strong> Paytm, PhonePe, Amazon Pay</li>
                <li><strong>Auto-Debit (NACH):</strong> Automated monthly debit from bank account</li>
              </ul>
            </section>

            {/* Payment Security */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">4. Payment Security</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                We implement industry-leading security measures to protect your payment information:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>PCI-DSS Compliance:</strong> All payment gateways are PCI-DSS Level 1 certified</li>
                <li><strong>SSL Encryption:</strong> 256-bit SSL encryption for all transactions</li>
                <li><strong>Tokenization:</strong> Card details are tokenized and never stored on our servers</li>
                <li><strong>Two-Factor Authentication:</strong> OTP verification for all transactions</li>
                <li><strong>3D Secure:</strong> Additional authentication layer for card payments</li>
                <li><strong>Fraud Detection:</strong> Real-time fraud monitoring and risk assessment</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                <strong>Important:</strong> Rebuilt never asks for your CVV, card PIN, OTP, or full card details via email, SMS, or phone calls.
              </p>
            </section>

            {/* Payment Processing */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">5. Payment Processing and Timeline</h3>
              
              <p className="text-gray-700 font-semibold mb-3">5.1 Disbursement Timeline</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li><strong>IMPS:</strong> Instant to 30 minutes</li>
                <li><strong>NEFT:</strong> Within 2 hours during working hours</li>
                <li><strong>RTGS:</strong> Within 30 minutes (for amounts ≥ ₹2 lakh)</li>
              </ul>

              <p className="text-gray-700 font-semibold mb-3">5.2 Repayment Processing</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>UPI/Wallets:</strong> Instant confirmation</li>
                <li><strong>Debit/Credit Cards:</strong> Instant to 2 business days</li>
                <li><strong>Net Banking:</strong> Instant to 24 hours</li>
                <li><strong>Auto-Debit (NACH):</strong> Processed on due date, confirmation within 2 days</li>
              </ul>
            </section>

            {/* Transaction Charges */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">6. Transaction Charges</h3>
              
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>6.1 Disbursement Charges:</strong> Rebuilt bears all disbursement transaction charges. No charges are deducted from your loan amount.
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>6.2 Repayment Charges:</strong>
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>UPI, Net Banking, Debit Cards:</strong> No transaction charges</li>
                <li><strong>Credit Cards:</strong> 1.5% - 2% convenience fee may apply</li>
                <li><strong>Wallets:</strong> No transaction charges</li>
                <li><strong>NACH Auto-Debit:</strong> No transaction charges</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                <em>Note: Actual charges may vary based on payment gateway. Charges will be clearly displayed before transaction confirmation.</em>
              </p>
            </section>

            {/* Failed Transactions */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">7. Failed Transactions</h3>
              
              <p className="text-gray-700 font-semibold mb-3">7.1 Common Reasons for Failure</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-4">
                <li>Insufficient balance in account</li>
                <li>Daily transaction limit exceeded</li>
                <li>Incorrect OTP or authentication failure</li>
                <li>Technical issues with bank or payment gateway</li>
                <li>Expired card or blocked account</li>
              </ul>

              <p className="text-gray-700 font-semibold mb-3">7.2 What Happens to Your Money</p>
              <p className="text-gray-700 leading-relaxed mb-3">
                If your payment fails but money is debited from your account:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>The amount will be automatically refunded by the payment gateway</li>
                <li>Refund timeline: 5-7 business days</li>
                <li>If not received within 7 days, contact support@rebuilt.in</li>
              </ul>
            </section>

            {/* Auto-Debit (NACH) */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">8. Auto-Debit (NACH) Mandate</h3>
              
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>8.1 What is NACH?</strong> National Automated Clearing House (NACH) is an automated payment system that allows us to debit your bank account on scheduled EMI due dates.
              </p>

              <p className="text-gray-700 font-semibold mb-3">8.2 How It Works</p>
              <ol className="list-decimal pl-6 space-y-2 text-gray-700 mb-4">
                <li>You authorize Rebuilt to set up an auto-debit mandate during loan acceptance</li>
                <li>You sign the NACH mandate electronically or physically</li>
                <li>Bank approves and activates the mandate (2-3 business days)</li>
                <li>On each EMI due date, the amount is automatically debited</li>
                <li>You receive SMS/email confirmation of each debit</li>
              </ol>

              <p className="text-gray-700 font-semibold mb-3">8.3 Managing NACH Mandate</p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>Modify:</strong> Contact support to change bank account or debit amount</li>
                <li><strong>Cancel:</strong> You may cancel the mandate, but manual payment will be required</li>
                <li><strong>Failed Debit:</strong> Ensure sufficient balance 1 day before due date</li>
              </ul>
            </section>

            {/* Payment Disputes */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">9. Payment Disputes and Chargebacks</h3>
              
              <p className="text-gray-700 leading-relaxed mb-3">
                <strong>9.1 Unauthorized Transactions:</strong> If you notice an unauthorized transaction:
              </p>
              <ol className="list-decimal pl-6 space-y-2 text-gray-700 mb-4">
                <li>Immediately email support@rebuilt.in with transaction details</li>
                <li>Change your account password and payment credentials</li>
                <li>We will investigate within 3-5 business days</li>
                <li>If confirmed unauthorized, full refund will be processed</li>
              </ol>

              <p className="text-gray-700 font-semibold mb-3">9.2 Chargeback Policy</p>
              <p className="text-gray-700 leading-relaxed">
                Initiating a chargeback without contacting us first may result in account suspension. Please contact our support team to resolve disputes before filing a chargeback with your bank.
              </p>
            </section>

            {/* Data Protection */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">10. Payment Data Protection</h3>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li><strong>No Storage:</strong> We do not store your card details, CVV, or PIN on our servers</li>
                <li><strong>Tokenization:</strong> Payment information is tokenized by the gateway</li>
                <li><strong>Encryption:</strong> All data in transit is encrypted using TLS 1.2+</li>
                <li><strong>Access Control:</strong> Strict role-based access to payment systems</li>
                <li><strong>Audit Logs:</strong> All payment transactions are logged for security audits</li>
              </ul>
            </section>

            {/* Customer Support */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">11. Payment Support</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                For payment-related queries, issues, or disputes:
              </p>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-gray-800"><strong>Payment Support Email:</strong> payments@rebuilt.in</p>
                <p className="text-gray-800"><strong>Customer Support:</strong> support@rebuilt.in</p>
                <p className="text-gray-800"><strong>Phone:</strong> +91-XXXX-XXXXXX</p>
                <p className="text-gray-800"><strong>Working Hours:</strong> 24/7 for payment emergencies</p>
              </div>
            </section>

            {/* Compliance */}
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">12. Regulatory Compliance</h3>
              <p className="text-gray-700 leading-relaxed">
                Our payment processing complies with:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Reserve Bank of India (RBI) payment system guidelines</li>
                <li>Payment Card Industry Data Security Standard (PCI-DSS)</li>
                <li>Information Technology Act, 2000</li>
                <li>National Payments Corporation of India (NPCI) regulations</li>
              </ul>
            </section>
          </div>

          {/* Back to Home Button */}
          <div className="mt-12 pt-8 border-t border-gray-200">
            <button
              onClick={() => navigate('/')}
              className="w-full sm:w-auto px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
